import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmt, fmtPiles, fmtDateTime, formatApiError } from "@/lib/api";
import { OPS } from "@/lib/ops";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const ACCENT_BAR = { amber: "bg-amber-500", green: "bg-green-600", red: "bg-red-600", blue: "bg-blue-700" };

export default function MovementsList({ onEdit, refreshKey, onChanged, canEditFn, canDeleteFn }) {
  const { data = [], refetch } = useQuery({
    queryKey: ["movements", refreshKey],
    queryFn: async () => (await api.get("/movements")).data,
  });
  const { data: types = [] } = useQuery({
    queryKey: ["packaging-types"],
    queryFn: async () => (await api.get("/packaging-types")).data,
  });
  const pileMap = Object.fromEntries(types.map((t) => [t.code, t.pile_size || 1]));
  const piles = (arr) => (arr || []).map((x) => `${x.packaging_type}: ${fmtPiles(x.quantity, pileMap[x.packaging_type], true)}`).join(" · ");
  const units = (arr) => (arr || []).map((x) => `${x.packaging_type}: ${fmt(x.quantity)} u.`).join(" · ");

  const del = async (m) => {
    if (!window.confirm("Supprimer définitivement cette saisie ?")) return;
    try {
      await api.delete(`/movements/${m.id}`);
      toast.success("Saisie supprimée.");
      refetch();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const bordereau = async (m) => {
    try {
      const res = await api.get(`/movements/${m.id}/bordereau.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bordereau_${m.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const detail = (m) => {
    if (m.type === "LAVAGE" || m.type === "LAVAGE_END") {
      return `Lavés ${piles(m.lines_lavees) || "—"} · Rebuts ${units(m.lines_rebutees) || "—"}`;
    }
    if (m.type === "PERTE") {
      return `Casse ${units(m.lines) || "—"}`;
    }
    const pt = m.lines_perte_transport?.length ? ` · Perte transport ${units(m.lines_perte_transport)}` : "";
    return (piles(m.lines) || "—") + pt;
  };

  return (
    <div className="rounded-xl border-2 border-slate-300 bg-white shadow-sm divide-y divide-slate-100" data-testid="movements-list">
      {data.length === 0 && <p className="p-4 text-slate-500 text-sm">Aucune saisie.</p>}
      {data.map((m) => {
        const op = OPS[m.type] || { label: m.type, accent: "blue" };
        return (
          <div key={m.id} className="p-3 flex items-start gap-3" data-testid={`movement-${m.id}`}>
            <span className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${ACCENT_BAR[op.accent]}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-bold text-slate-800">{op.label}</span>
                {m.ecart && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">ÉCART</span>}
                <span className="text-xs text-slate-400 font-data">{fmtDateTime(m.date)}</span>
              </div>
              <div className="text-sm text-slate-600 font-data truncate">{detail(m)}</div>
              <div className="text-xs text-slate-400">
                {[m.from_site && `${m.from_site}→${m.to_site || ""}`, m.site, m.motif, m.created_by_name]
                  .filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              {m.type === "ENVOI_SALES" && (
                <Button size="icon" variant="ghost" className="h-9 w-9 text-blue-700" onClick={() => bordereau(m)} data-testid={`bordereau-${m.id}`} title="Bordereau d'envoi (PDF)" aria-label="Télécharger le bordereau d'envoi (PDF)">
                  <FileText className="h-4 w-4" />
                </Button>
              )}
              {m.can_edit && (!canEditFn || canEditFn(m)) && (
                <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => onEdit(m)} data-testid={`edit-${m.id}`} title="Modifier la saisie" aria-label="Modifier la saisie">
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {m.can_edit && (!canDeleteFn || canDeleteFn(m)) && (
                <Button size="icon" variant="ghost" className="h-9 w-9 text-red-600" onClick={() => del(m)} data-testid={`delete-${m.id}`} title="Supprimer la saisie" aria-label="Supprimer la saisie">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
